import { useEffect, useState } from "react";
import { BadgeCheck, MessageSquareText, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface HomepageReview {
  id: string;
  customer_display_name: string;
  product_key: string | null;
  rating: number;
  title: string;
  review_text: string;
  created_at: string;
  is_verified_purchase: boolean;
  photo_urls: string[] | null;
}

interface HomeReviewCarouselProps {
  onWriteReview: () => void;
}

export function HomeReviewCarousel({ onWriteReview }: HomeReviewCarouselProps) {
  const [reviews, setReviews] = useState<HomepageReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [api, setApi] = useState<CarouselApi>();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [slideCount, setSlideCount] = useState(0);

  useEffect(() => {
    let active = true;

    const loadReviews = async () => {
      const { data, error } = await supabase
        .from("reviews_public")
        .select("id, customer_display_name, product_key, rating, title, review_text, created_at, is_verified_purchase, photo_urls")
        .eq("product_key", "brainCharge")
        .order("created_at", { ascending: false })
        .limit(24);

      if (!active) return;
      if (error) {
        console.error("Error fetching homepage reviews:", error);
      } else {
        setReviews((data ?? []) as HomepageReview[]);
      }
      setLoading(false);
    };

    void loadReviews();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!api) return;

    const updatePosition = () => {
      setSelectedIndex(api.selectedScrollSnap());
      setSlideCount(api.scrollSnapList().length);
    };

    updatePosition();
    api.on("select", updatePosition);
    api.on("reInit", updatePosition);
    return () => {
      api.off("select", updatePosition);
      api.off("reInit", updatePosition);
    };
  }, [api]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" aria-label="Loading customer reviews">
        {[1, 2, 3].map((item) => (
          <Card key={item} className="min-h-[300px] animate-pulse border-border/70 bg-card">
            <CardContent className="space-y-5 p-6">
              <div className="h-4 w-28 rounded bg-muted" />
              <div className="h-6 w-3/4 rounded bg-muted" />
              <div className="h-20 rounded bg-muted" />
              <div className="h-10 rounded bg-muted" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="border-y border-border/70 bg-card/50 px-5 py-10 text-center md:px-8">
        <MessageSquareText className="mx-auto mb-4 h-8 w-8 text-primary" aria-hidden="true" />
        <h2 className="text-xl font-bold text-foreground">No approved reviews yet</h2>
        <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
          Be the first to share an honest experience with Brain Charge™.
        </p>
        <Button className="mt-5" onClick={onWriteReview}>
          Write a Review
        </Button>
      </div>
    );
  }

  return (
    <div>
      <Carousel
        setApi={setApi}
        opts={{ align: "start", containScroll: "trimSnaps" }}
        className="mx-auto w-full"
        aria-label="Approved Brain Charge customer reviews"
      >
        <CarouselContent className="-ml-3 md:-ml-4">
          {reviews.map((review, index) => (
            <CarouselItem
              key={review.id}
              className="basis-full pl-3 md:basis-1/2 md:pl-4 lg:basis-1/3"
              aria-label={`${index + 1} of ${reviews.length}`}
            >
              <Card className="h-full min-h-[310px] overflow-hidden border-border/80 bg-card shadow-lg shadow-primary/5">
                <CardContent className="flex h-full flex-col p-5 md:p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex gap-0.5" aria-label={`${review.rating} out of 5 stars`}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={cn(
                            "h-4 w-4",
                            star <= review.rating
                              ? "fill-primary text-primary"
                              : "fill-muted text-muted-foreground/40",
                          )}
                          aria-hidden="true"
                        />
                      ))}
                    </div>
                    {review.product_key === "brainCharge" && (
                      <Badge variant="outline" className="shrink-0 border-accent/40 bg-accent/10 text-accent-foreground">
                        Brain Charge™
                      </Badge>
                    )}
                  </div>

                  <h3 className="mt-5 text-lg font-bold leading-snug text-foreground line-clamp-2">
                    {review.title}
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-muted-foreground line-clamp-5">
                    {review.review_text}
                  </p>

                  {review.photo_urls && review.photo_urls.length > 0 && (
                    <div className="mt-4 flex gap-2" aria-label="Customer review photos">
                      {review.photo_urls.slice(0, 3).map((url, photoIndex) => (
                        <img
                          key={`${url}-${photoIndex}`}
                          src={url}
                          alt={`Photo from ${review.customer_display_name}'s review`}
                          loading="lazy"
                          className="h-12 w-12 rounded border border-border object-cover"
                        />
                      ))}
                    </div>
                  )}

                  <div className="mt-auto flex items-end justify-between gap-3 border-t border-border/70 pt-5">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-foreground">{review.customer_display_name}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {new Date(review.created_at).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                    {review.is_verified_purchase && (
                      <Badge className="shrink-0 gap-1 bg-secondary text-secondary-foreground">
                        <BadgeCheck className="h-3.5 w-3.5" aria-hidden="true" />
                        Verified Purchase
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious
          className="left-2 top-1/2 h-10 w-10 border-primary/40 bg-background/95 shadow-lg md:-left-5"
          aria-label="Previous customer reviews"
        />
        <CarouselNext
          className="right-2 top-1/2 h-10 w-10 border-primary/40 bg-background/95 shadow-lg md:-right-5"
          aria-label="Next customer reviews"
        />
      </Carousel>

      {slideCount > 1 && (
        <div className="mt-5 flex items-center justify-center gap-2" aria-label={`Review group ${selectedIndex + 1} of ${slideCount}`}>
          {Array.from({ length: slideCount }).map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => api?.scrollTo(index)}
              className={cn(
                "h-2 rounded-full transition-all",
                selectedIndex === index ? "w-6 bg-primary" : "w-2 bg-muted-foreground/35 hover:bg-muted-foreground/60",
              )}
              aria-label={`Go to review group ${index + 1}`}
              aria-current={selectedIndex === index ? "true" : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}